<template lang="pug">
  .page.details-page
    .wrapper(v-if="!recordNotFound")
      back-to-search
      header-section(
        title="LEI Reference Data"
        simple
        :tooltip="tooltips.referenceData"
      )
        template(v-if="hasBEDAsManagingLou" v-slot:notice="notice")
          lei-notice(
            type="info"
            text="This record is currently not available for challenge management."
          )
          lei-notice(
            data-test-id="lei-disclaimer"
            type="info"
            text=`This record is no longer actively managed by Business Entity Data B.V. 
            This organization is released from responsibility and listed for technical reasons only 
            until this LEI is transferred by the Legal Entity to another LEI Issuing Organization. 
            For questions, please contact <a href="mailto:info@gleif.org">info@gleif.org</a>.`
          )
        toggle-challenge(v-else)
      title-section

      router-view(name="challengePage")
      .container(v-if="!challengeMode")
        el-tabs.lei-tabs(
          :value="activeTab"
          v-if='!challengeMode'
          type="card"
          :before-leave="tabSwitch"
          )
          el-tab-pane(
            label="Current Data"
            key="record"
            name="record"
            )
            record-info(v-if="!challengeMode")
            record-info-lvl2-funds(v-if="!challengeMode")
            record-info-lvl2-branches(v-if="!challengeMode")
            record-info-lvl2-parents(v-if="!challengeMode")
            record-info-lvl2-children(v-if="!challengeMode")
            record-modals
          el-tab-pane(
            label="Events and Changes"
            key="record_modifications"
            name="record_modifications"
            )
            record-modifications-info(v-if="!challengeMode")
            record-not-found(v-else key="not-found")
          el-tab-pane(
            label="Show XML"
            key="show_xml"
            name="show_xml"
          )
            record-xml(v-if="!challengeMode")
            record-not-found(v-else key="not-found")
          el-tab-pane(
            label="vLEIs"
            key="verifiable_credentials"
            name="verifiable_credentials"
            )
            credential-record-info(v-if="!challengeMode")
            credential-notice(v-if="credentials.length")
    record-not-found(v-else key="not-found")
    record-modals
    side-bar-panel(v-if="needSideBar")
</template>
<script>
import record from '@/constants/tooltips/record'
import { mapActions, mapMutations, mapState, mapGetters, } from 'vuex'
import { CLEAR_CHALLENGE, } from '@/store/modules/record/modules/challenge/mutations-types'
import { RESET_RECORD_INFO, } from '@/store/modules/record/mutations-types'
import Challenge from '@/mixins/challenge'
import HeaderSection from '@/components/layout-components/HeaderSection'
import TitleSection from '@/components/record-details/TitleSection'
import OptionsSection from '@/components/record-details/OptionsSection'
import SideBarPanel from '@/components/layout-components/SideBarPanel'
import BackToSearch from '@/components/record-details/BackToSearch'
import RecordNotFound from '@/components/record-details/RecordNotFound'
import RecordInfo from '@/components/record-details/RecordInfo'
import RecordInfoLvl2Parents from '@/components/record-details/RecordInfoLvl2Parents'
import RecordInfoLvl2Children from '@/components/record-details/RecordInfoLvl2Children'
import RecordInfoLvl2Funds from '@/components/record-details/RecordInfoLvl2Funds'
import RecordInfoLvl2Branches from '@/components/record-details/RecordInfoLvl2Branches'
import RecordModals from '@/components/record-details/RecordModals'
import RecordModificationsInfo from '@/components/record-details/modifications/RecordModificationsInfo'
import RecordXml from '@/components/record-details/RecordXml'
import CredentialRecordInfo from '@/components/record-details/credentials/CredentialRecordInfo'
import ToggleChallenge from '@/components/challenge-components/ToggleChallenge'
import CredentialNotice from '@/components/record-details/credentials/CredentialNotice'
import LeiNotice from '@/components/lei-components/LeiNotice'

export default {
  components: {
    RecordInfoLvl2Funds,
    RecordInfoLvl2Branches,
    RecordModals,
    RecordInfo,
    RecordNotFound,
    RecordInfoLvl2Parents,
    RecordInfoLvl2Children,
    RecordXml,
    BackToSearch,
    SideBarPanel,
    TitleSection,
    OptionsSection,
    HeaderSection,
    RecordModificationsInfo,
    ToggleChallenge,
    CredentialRecordInfo,
    CredentialNotice,
    LeiNotice,
  },
  mixins: [ Challenge, ],
  data () {
    return {
      confirmText: 'You are leaving this page, if you proceed all entered data will be lost. ' +
          'Do you want to leave this page?',
      tooltips: record,
      activeTab: this.$route.params.currentTab || 'record',
      modificationsTypes: [ 'lei', 'rr', 'repex', ],
      page: 1,
    }
  },
  computed: {
    needConfirm () {
      return this.challengeMode
    },
    needSideBar () {
      return this.$route.params.tab !== 'relationship_data' && this.$route.params.tab !== 'show_xml'
    },
    ...mapState('record', [ 'recordNotFound', 'recordModifications', 'xmlData', ]),
    ...mapState('credential', [ 'credentials', 'pagination', 'lei', ]),
    ...mapGetters('record', [ 'hasBEDAsManagingLou', ]),
  },
  watch: {
    async '$route' (to, from) {
      (Boolean(to.params.currentTab) && to.params.currentTab !== this.activeTab)
        ? this.activeTab = to.params.currentTab
        : this.activeTab = 'record'

      if (to.params.id !== from.params.id) {
        await this.getRecord(this.$route.params.id)
      }
    },
  },
  beforeRouteLeave (to, from, next) {
    if (!this.needConfirm) {
      next()
    } else {
      this.$confirm(this.confirmText, {
        type: 'warning',
      }).then(() => {
        this.clearChallenge()
        this.$message({
          type: 'info',
          message: 'Challenge canceled',
        })
        next()
      }).catch(() => {
        this.$message({
          type: 'info',
          message: 'Challenge continues',
        })
      })
    }

    if (from.params.id && to.params.id && from.params.id !== to.params.id) {
      this.resetRecordInfo()
    }
  },
  async created () {
    this.getFilters()
    await this.getRecord(this.$route.params.id)
    this.getModificationData()
    this.getXmlRecord()
    this.getCredentialsData()
  },
  mounted () {
    window.addEventListener('beforeunload', this.preventReloud)
  },
  beforeDestroy () {
    window.removeEventListener('beforeunload', this.preventReloud)
  },
  methods: {
    preventReloud (e) {
      if (this.needConfirm) {
        e.preventDefault()
        e.returnValue = ''
      }
    },
    tabSwitch (activeTab) {
      this.routerTabChange(activeTab)
      this.getModificationData()
      this.getXmlRecord()
      this.getCredentialsData()
    },
    getModificationData () {
      if (this.$route.params.currentTab === 'record_modifications' && !this.recordModifications.changed) {
        const id = this.$route.params.id

        this.modificationsTypes.forEach((item) => {
          this.getRecordModifications({
            id,
            page: 1,
            type: item,
          })
        })
      }
    },
    getCredentialsData () {
      const id = this.$route.params.id
      if (this.$route.params.currentTab === 'verifiable_credentials' && this.lei !== id) {
        this.getCredentials({
          lei: id,
        })
      }
    },
    getXmlRecord () {
      const id = this.$route.params.id
      if (this.$route.params.currentTab === 'show_xml' && this.xmlData.lei.record.lei !== id) {
        this.getRecordXml({ id, })
        this.getRelationshipXmlData(id)
      }
    },
    async getRelationshipXmlData (id) {
      await Promise.all([
        /* eslint-disable */
        // rr.getRelationship
        this.getRelationshipOrRepexRecordXml({ id, relationshipType: 'directParent', }),
        this.getRelationshipOrRepexRecordXml({ id, relationshipType: 'ultimateParent', }),
        this.getRelationshipOrRepexRecordXml({ id, relationshipType: 'fundManager', }),
        this.getRelationshipOrRepexRecordXml({ id, relationshipType: 'umbrellaFund', }),
        this.getRelationshipOrRepexRecordXml({ id, relationshipType: 'masterFund', }),
        this.getRelationshipOrRepexRecordXml({ id, relationshipType: 'headOffice', }),
        // repex.relationship
        this.getRelationshipOrRepexRecordXml({ id, relationshipType: 'directParentException', }),
        this.getRelationshipOrRepexRecordXml({ id, relationshipType: 'ultimateParentException',})
      ])
    },
    routerTabChange (currentTab) {
      this.$router.push({
        name: 'RecordDetailsPage',
        params: { currentTab, },
      })
    },

    ...mapActions('search', [ 'getFilters', ]),
    ...mapActions('record', [
      'getRecord',
      'getRecordModifications',
      'getRecordXml',
      'getRelationshipOrRepexRecordXml',
    ]),
    ...mapActions('credential', [ 'getCredentials', 'getRoleCredentials', ]),
    ...mapMutations('record/challenge', {
      clearChallenge: CLEAR_CHALLENGE,
      resetRecordInfo: RESET_RECORD_INFO,
    }),
  },
}
</script>
<style lang="stylus" scoped>
  @import "../assets/styles/variables.styl"
  .details-page
    .wrapper
      padding 0

    .lei-btn
      height 45px
      display inline-flex
      align-items center
      justify-content center
      padding 10px 20px
      background-color $primary-color
      font-size 95%
      font-weight bold
      color $white-color
</style>



// WEBPACK FOOTER //
// src/pages/RecordDetailsPage.vue